/**
 * Override de Login.jsx para SaaS.
 *
 * Añade:
 * - Tab "Crear cuenta" con formulario de registro
 * - Botón "Continuar con Google" (OAuth2 PKCE)
 * - Soporte para abrir registro vía query string: ?tab=register
 * - Redirección a invitación pendiente si existe en sessionStorage
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { FaEnvelope, FaEye, FaEyeSlash, FaGoogle, FaLock, FaUser } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { APP_NAME, APP_TAGLINE } from '../config/runtime';
import { loginUser, registerUser } from '../services';
import apiClient from '../services/apiClient';
import Logo from './Logo';

const PENDING_INVITE_KEY = 'pendingInviteToken';

const isSuperAdminToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1] || ''));
    return payload?.is_super_admin === true;
  } catch {
    return false;
  }
};

const Login = ({ onLogin }) => {
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [showLoginPw, setShowLoginPw] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const [regData, setRegData] = useState({ email: '', full_name: '', password: '', confirm: '' });
  const [showRegPw, setShowRegPw] = useState(false);
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState('');

  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    apiClient.get('/v1/auth/config')
      .then((response) => setGoogleEnabled(response.data.google_oauth_enabled === true))
      .catch(() => setGoogleEnabled(false));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setTabIndex(params.get('tab') === 'register' ? 1 : 0);
  }, [location.search]);

  const afterLogin = (isSuperAdmin = false) => {
    if (onLogin) onLogin(true);
    const pendingToken = sessionStorage.getItem(PENDING_INVITE_KEY);
    setTimeout(() => {
      if (pendingToken) {
        navigate(`/invite?token=${encodeURIComponent(pendingToken)}`);
      } else if (isSuperAdmin) {
        // In this deployed frontend, /users is the administrative entry point.
        navigate('/users');
      } else {
        navigate('/');
      }
    }, 800);
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    if (!loginData.username || !loginData.password) {
      setLoginError('Por favor completa todos los campos');
      return;
    }

    setLoginLoading(true);
    setLoginError('');

    try {
      const response = await loginUser(loginData);
      authLogin(response.access_token);
      toast({
        title: 'Bienvenido',
        description: 'Inicio de sesión exitoso',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      afterLogin(isSuperAdminToken(response.access_token));
    } catch (error) {
      setLoginError(error.message || 'Usuario o contraseña incorrectos');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegSubmit = async (event) => {
    event.preventDefault();
    setRegError('');

    if (regData.password !== regData.confirm) {
      setRegError('Las contraseñas no coinciden');
      return;
    }

    setRegLoading(true);

    try {
      await registerUser({
        email: regData.email,
        password: regData.password,
        full_name: regData.full_name.trim() || regData.email.split('@')[0],
      });

      const response = await loginUser({ username: regData.email, password: regData.password });
      authLogin(response.access_token);

      toast({
        title: '¡Cuenta creada!',
        description: `Bienvenido a ${APP_NAME}`,
        status: 'success',
        duration: 2500,
        isClosable: true,
      });

      afterLogin(isSuperAdminToken(response.access_token));
    } catch (error) {
      setRegError(error.message || 'Error al crear la cuenta');
    } finally {
      setRegLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      const response = await apiClient.get('/v1/auth/google');
      window.location.href = response.data.authorization_url;
    } catch {
      toast({
        title: 'Error con Google',
        description: 'No se pudo conectar con Google. Verifica la configuración.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setGoogleLoading(false);
    }
  };

  const GoogleSection = () => (
    <Button
      w="full"
      bg="gray.800"
      color="white"
      leftIcon={<FaGoogle />}
      onClick={handleGoogleLogin}
      isLoading={googleLoading}
      loadingText="Conectando..."
      _hover={{ bg: 'gray.700', transform: 'translateY(-1px)', boxShadow: 'md' }}
      _active={{ bg: 'gray.900' }}
      transition="all 0.2s"
      fontWeight="medium"
      borderRadius="lg"
      h="42px"
    >
      Continuar con Google
    </Button>
  );

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bgGradient="linear(to-b, indigo.800, indigo.900)"
      position="relative"
      px={4}
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgImage: 'radial-gradient(circle at 25px 25px, rgba(255,255,255,0.05) 2%, transparent 0%), linear-gradient(135deg, rgba(79,70,229,0.1) 0%, rgba(0,0,0,0) 50%)',
        backgroundSize: '30px 30px, cover',
        opacity: 0.6,
      }}
    >
      <Card
        maxW="500px"
        w="full"
        bg={cardBg}
        borderColor="rgba(79,70,229,0.3)"
        borderWidth="1px"
        borderRadius="xl"
        shadow="2xl"
        overflow="hidden"
        _hover={{ boxShadow: '0 0 20px rgba(79,70,229,0.2)' }}
        transition="box-shadow 0.3s ease"
      >
        <CardBody p={6}>
          <VStack spacing={5} align="center">
            {/* Header compacto: logo + título en fila */}
            <HStack spacing={3} justify="center" pt={2}>
              <Box
                display="flex"
                alignItems="center"
                filter="drop-shadow(0px 3px 4px rgba(0,0,0,0.2))"
                boxSize="52px"
                overflow="hidden"
                flexShrink={0}
              >
                <Logo size="52px" cropScale={1.2} />
              </Box>
              <VStack spacing={0} align="flex-start">
                <Heading size="md" color="indigo.600" fontWeight="bold" letterSpacing="tight">
                  ThreatModeling
                  <Text as="span" color="teal.400">
                    .studio
                  </Text>
                </Heading>
                <Text fontSize="xs" color="gray.500" letterSpacing="wider">
                  {APP_TAGLINE}
                </Text>
              </VStack>
            </HStack>

            {/* Botón Google antes de los tabs */}
            {googleEnabled && <GoogleSection />}

            {googleEnabled && (
              <HStack w="full" align="center">
                <Divider borderColor={borderColor} />
                <Text fontSize="xs" color="gray.400" whiteSpace="nowrap" px={2}>
                  o con email
                </Text>
                <Divider borderColor={borderColor} />
              </HStack>
            )}

            <Tabs w="full" colorScheme="indigo" isFitted index={tabIndex} onChange={setTabIndex}>
              <TabList mb={4}>
                <Tab fontSize="sm" fontWeight="medium" _selected={{ color: 'indigo.700', borderColor: 'indigo.500' }}>
                  Iniciar sesión
                </Tab>
                <Tab fontSize="sm" fontWeight="medium" _selected={{ color: 'indigo.700', borderColor: 'indigo.500' }}>
                  Crear cuenta
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel p={0}>
                  <VStack spacing={4}>
                    <Box as="form" onSubmit={handleLoginSubmit} w="full">
                      <VStack spacing={4}>
                        {loginError && (
                          <Alert status="error" borderRadius="md">
                            <AlertIcon />
                            {loginError}
                          </Alert>
                        )}

                        <FormControl isRequired>
                          <FormLabel fontSize="sm" color="gray.600">Email / Usuario</FormLabel>
                          <InputGroup>
                            <InputLeftElement pointerEvents="none">
                              <FaUser color="gray" />
                            </InputLeftElement>
                            <Input
                              type="text"
                              name="username"
                              value={loginData.username}
                              onChange={(event) => setLoginData({ ...loginData, username: event.target.value })}
                              placeholder="tu@email.com"
                              borderColor={borderColor}
                              _hover={{ borderColor: 'indigo.400' }}
                              _focus={{ borderColor: 'indigo.500', boxShadow: '0 0 0 1px var(--chakra-colors-indigo-500)' }}
                              bg="gray.50"
                            />
                          </InputGroup>
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel fontSize="sm" color="gray.600">Contraseña</FormLabel>
                          <InputGroup>
                            <InputLeftElement pointerEvents="none">
                              <FaLock color="gray" />
                            </InputLeftElement>
                            <Input
                              type={showLoginPw ? 'text' : 'password'}
                              name="password"
                              value={loginData.password}
                              onChange={(event) => setLoginData({ ...loginData, password: event.target.value })}
                              placeholder="Tu contraseña"
                              borderColor={borderColor}
                              _hover={{ borderColor: 'indigo.400' }}
                              _focus={{ borderColor: 'indigo.500', boxShadow: '0 0 0 1px var(--chakra-colors-indigo-500)' }}
                              bg="gray.50"
                            />
                            <InputRightElement>
                              <IconButton
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowLoginPw(!showLoginPw)}
                                icon={showLoginPw ? <FaEyeSlash /> : <FaEye />}
                                aria-label={showLoginPw ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                              />
                            </InputRightElement>
                          </InputGroup>
                        </FormControl>

                        <Button
                          type="submit"
                          bgGradient="linear(to-r, indigo.500, indigo.700)"
                          _hover={{
                            bgGradient: 'linear(to-r, indigo.600, indigo.800)',
                            transform: 'translateY(-1px)',
                            boxShadow: 'lg',
                          }}
                          size="lg"
                          w="full"
                          mt={2}
                          isLoading={loginLoading}
                          loadingText="Iniciando sesión..."
                          color="white"
                          transition="all 0.2s"
                          fontWeight="medium"
                          letterSpacing="wide"
                        >
                          Iniciar Sesión
                        </Button>
                      </VStack>
                    </Box>
                  </VStack>
                </TabPanel>

                <TabPanel p={0}>
                  <VStack spacing={4}>
                    <Box as="form" onSubmit={handleRegSubmit} w="full">
                      <VStack spacing={4}>
                        {regError && (
                          <Alert status="error" borderRadius="md">
                            <AlertIcon />
                            {regError}
                          </Alert>
                        )}

                        <FormControl isRequired>
                          <FormLabel fontSize="sm" color="gray.600">Email</FormLabel>
                          <InputGroup>
                            <InputLeftElement pointerEvents="none">
                              <FaEnvelope color="gray" />
                            </InputLeftElement>
                            <Input
                              type="email"
                              name="email"
                              value={regData.email}
                              onChange={(event) => setRegData({ ...regData, email: event.target.value })}
                              placeholder="tu@email.com"
                              borderColor={borderColor}
                              _hover={{ borderColor: 'indigo.400' }}
                              _focus={{ borderColor: 'indigo.500', boxShadow: '0 0 0 1px var(--chakra-colors-indigo-500)' }}
                              bg="gray.50"
                            />
                          </InputGroup>
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm" color="gray.600">Nombre completo</FormLabel>
                          <InputGroup>
                            <InputLeftElement pointerEvents="none">
                              <FaUser color="gray" />
                            </InputLeftElement>
                            <Input
                              type="text"
                              name="full_name"
                              value={regData.full_name}
                              onChange={(event) => setRegData({ ...regData, full_name: event.target.value })}
                              placeholder="Tu nombre (opcional)"
                              borderColor={borderColor}
                              _hover={{ borderColor: 'indigo.400' }}
                              _focus={{ borderColor: 'indigo.500', boxShadow: '0 0 0 1px var(--chakra-colors-indigo-500)' }}
                              bg="gray.50"
                            />
                          </InputGroup>
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel fontSize="sm" color="gray.600">Contraseña</FormLabel>
                          <InputGroup>
                            <InputLeftElement pointerEvents="none">
                              <FaLock color="gray" />
                            </InputLeftElement>
                            <Input
                              type={showRegPw ? 'text' : 'password'}
                              name="password"
                              value={regData.password}
                              onChange={(event) => setRegData({ ...regData, password: event.target.value })}
                              placeholder="Mín. 8 caracteres"
                              borderColor={borderColor}
                              _hover={{ borderColor: 'indigo.400' }}
                              _focus={{ borderColor: 'indigo.500', boxShadow: '0 0 0 1px var(--chakra-colors-indigo-500)' }}
                              bg="gray.50"
                            />
                            <InputRightElement>
                              <IconButton
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowRegPw(!showRegPw)}
                                icon={showRegPw ? <FaEyeSlash /> : <FaEye />}
                                aria-label={showRegPw ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                              />
                            </InputRightElement>
                          </InputGroup>
                          <FormHelperText fontSize="xs" color="gray.500">
                            Mín. 8 caracteres, 1 mayúscula, 1 número y 1 símbolo (!@#$%...)
                          </FormHelperText>
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel fontSize="sm" color="gray.600">Confirmar contraseña</FormLabel>
                          <InputGroup>
                            <InputLeftElement pointerEvents="none">
                              <FaLock color="gray" />
                            </InputLeftElement>
                            <Input
                              type="password"
                              name="confirm"
                              value={regData.confirm}
                              onChange={(event) => setRegData({ ...regData, confirm: event.target.value })}
                              placeholder="Repite la contraseña"
                              borderColor={borderColor}
                              _hover={{ borderColor: 'indigo.400' }}
                              _focus={{ borderColor: 'indigo.500', boxShadow: '0 0 0 1px var(--chakra-colors-indigo-500)' }}
                              bg="gray.50"
                            />
                          </InputGroup>
                        </FormControl>

                        <Button
                          type="submit"
                          bgGradient="linear(to-r, indigo.500, indigo.700)"
                          _hover={{
                            bgGradient: 'linear(to-r, indigo.600, indigo.800)',
                            transform: 'translateY(-1px)',
                            boxShadow: 'lg',
                          }}
                          size="lg"
                          w="full"
                          mt={2}
                          isLoading={regLoading}
                          loadingText="Creando cuenta..."
                          color="white"
                          transition="all 0.2s"
                          fontWeight="medium"
                          letterSpacing="wide"
                        >
                          Crear Cuenta
                        </Button>
                      </VStack>
                    </Box>
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </VStack>
        </CardBody>
      </Card>
    </Flex>
  );
};

export default Login;
